import React from 'react';
import classnames from 'classnames';

import "./manning-online-desk.scss";
import { ManningOnlineStore } from '../../store/ManningOnlineStore';
import { ReadingStack, ReadingStackDisplayType } from '../readingStack/ReadingStack';
import { TaggedProductList } from '../../../ts/components/common/taggedProductList/TaggedProductList';
import { ProductCard } from '../../../ts/components/common/productCard/ProductCard';
import { IProductDescription } from '../../../ts/models/product/IProductDescription';
import { IReadingList } from '../../models/ReadingList';
import { ReadingList } from '../../../ts/components/common/readingList/ReadingList';

export interface IProps {
    store: ManningOnlineStore;
}
export interface IState {
    isBeingDraggedOn: boolean;
    selectedReadingList: IReadingList;
}

export class ManningOnlineDesk extends React.Component <IProps, IState> {
    private hasBeenMounted: boolean;
    private root: HTMLElement;
    
    constructor(props: IProps) {
        super(props);

        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDragDrop = this.handleDragDrop.bind(this);

        this.handleReadingListRemoval = this.handleReadingListRemoval.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerEnd = this.handlePointerEnd.bind(this);
        this.handleSelectReadingList = this.handleSelectReadingList.bind(this);
        this.handleTaggedProductListRef = this.handleTaggedProductListRef.bind(this);
        this.handleUnselectReadingList = this.handleUnselectReadingList.bind(this);
        this.onRemoveProductClick = this.onRemoveProductClick.bind(this);
        this.onExploreDesktopClick = this.onExploreDesktopClick.bind(this);
        this.handleRef = this.handleRef.bind(this);
        
        this.state = {
            isBeingDraggedOn: false,
            selectedReadingList: null
        };
    }

    public componentDidMount(){
        this.hasBeenMounted = true;
    }

    public componentWillUnmount(){
        this.unsubscribeFromPointerEvents();
        this.hasBeenMounted = false;
    }

    private handleRef(root: HTMLElement) {
        this.root = root;
    }

    public render(){
        return <div className={classnames("manning-online-desk-root", {"_is-dragged-on": this.state.isBeingDraggedOn})}
                    ref={this.handleRef}
                    onDragEnter={this.handleDragEnter}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                    onDrop={this.handleDragDrop}>
                    { this.renderReadingLists() }
                    { this.renderSelectedReadingList()}
               </div>
    }

    private handleDragEnter(e){
        if(this.state.selectedReadingList) { return; }
        
        e.preventDefault();
        
        this.setState({
            isBeingDraggedOn: true
        });
    }

    private handleDragOver(e){
        if(this.state.selectedReadingList) { return; }
        
        e.preventDefault();

        if(!this.state.isBeingDraggedOn){
            const isTargetInReadingList = e.target && e.target.closest(".reading-stack-root") != null;
            if(isTargetInReadingList){ return; }

            this.setState({
                isBeingDraggedOn: true
            });

            this.props.store.triggerEveryReadingListDragEnter(-1);
        }
    }

    private handleDragLeave(e){
        if(this.state.selectedReadingList) { return; }
        
        this.setState({
            isBeingDraggedOn: false
        });
    }

    private handleDragDrop(e){
        if(this.state.selectedReadingList) { return; }
        
        e.preventDefault();
        let dataString = ""
        try {
            if(e.dataTransfer){
                dataString = e.dataTransfer.getData("text");
                const data = JSON.parse(dataString);
                const productId = data.productId;
                const deskElement: HTMLElement = e.currentTarget;
                
                this.props.store.createReadingListWithProduct(
                    productId, 
                    this.props.store.getNewUnnamedReadingListTitle(), {
                        x: e.pageX - (window.pageXOffset + deskElement.getBoundingClientRect().left) - data.x,
                        y: e.pageY - (window.pageYOffset + deskElement.getBoundingClientRect().top) - data.y
                    }
                );
            }
            
            this.handleDragLeave(e);
        }
        catch(e){
            console.log(e, dataString);
        }

        return false;
    }

    private renderReadingLists(){
        let readingLists = this.props.store.getDisplayedReadingLists();
        if(readingLists == null) { return null; }

        readingLists = readingLists.filter(it => it.title && it.products != null && it.products.length > 0);

        if(readingLists.length == 0){
            return <div className="_intro-message-container">
                        <div className="_message-header">your desktop</div>
                        <button className="_message-text" onClick={this.onExploreDesktopClick}>
                            explore <i className="fa fa-caret-right"></i>
                        </button>
                   </div>
        }

        return readingLists.map(it => {
            return <ReadingStack store={this.props.store}
                                 displayType={ReadingStackDisplayType.Stacked}
                                 readingList={it} 
                                 key={it.id + it.title}
                                 onRemoveReadingList={this.handleReadingListRemoval}
                                 onHandlePointerStart={e => this.handlePointerStart(e, it)}
                                 onAddToReadingList={productId => this.handleAddToReadingList(it, productId)}
                                 onSelectReadingList={this.handleSelectReadingList}
                                 onTitleDoubleClick={() => this.handleReadingListNameChange(it)}/>
        });
    }

    private renderSelectedReadingList(){
        const readingList = this.state.selectedReadingList;
        if(readingList == null) { return null; }

        return <ReadingList key={readingList.id} 
                            readingList={readingList} 
                            store={this.props.store}
                            onRef={this.handleTaggedProductListRef}
                            onCloseClick={this.handleUnselectReadingList}/>
    }

    private taggedProductListRoot: HTMLElement;
    private handleTaggedProductListRef(taggedProductList){
        if(taggedProductList){
            this.taggedProductListRoot = taggedProductList.closest(".tagged-product-list-with-details-root");
            setTimeout(() => {
                if(this.hasBeenMounted && this.taggedProductListRoot != null){
                    this.taggedProductListRoot.style.transformOrigin = `${this.state.selectedReadingList.position.x}px ${this.state.selectedReadingList.position.y}px `;
                    this.taggedProductListRoot.classList.add("appear");
                }
            }, 16);
        }
        else {
            this.taggedProductListRoot = null;
        }
    }

    private draggedReadingListInfo: {
        readingList: IReadingList;
        htmlElement: HTMLElement;
        
        overlappingHtmlElement: HTMLElement;
        overlappingReadingList: IReadingList;
        
        startingPoint: {
            top: number; 
            left: number;

            pageX: number;
            pageY: number;
        }
    } = null;
    
    private handlePointerStart(e, readingList: IReadingList){
        this.draggedReadingListInfo = {
            readingList: readingList,
            htmlElement: e.currentTarget,
            
            overlappingHtmlElement: null,
            overlappingReadingList: null,
            
            startingPoint: {
                top: parseInt(e.currentTarget.style.top),
                left: parseInt(e.currentTarget.style.left),
                pageX: e.pageX,
                pageY: e.pageY
            }
        };

        this.draggedReadingListInfo.htmlElement.classList.add("_is-being-dragged");

        document.addEventListener("mousemove", this.handlePointerMove);
        document.addEventListener("touchmove", this.handlePointerMove);

        document.addEventListener("mouseup", this.handlePointerEnd);
        document.addEventListener("touchend", this.handlePointerEnd);
        document.addEventListener("mouseleave", this.handlePointerEnd);
    }

    private handlePointerMove(e){
        if(this.draggedReadingListInfo == null) { return; }

        const x = this.draggedReadingListInfo.startingPoint.left + (e.pageX - this.draggedReadingListInfo.startingPoint.pageX);
        const y = this.draggedReadingListInfo.startingPoint.top + (e.pageY - this.draggedReadingListInfo.startingPoint.pageY);
        
        this.draggedReadingListInfo.htmlElement.style.left = `${x}px`;
        this.draggedReadingListInfo.htmlElement.style.top = `${y}px`;

        if(this.draggedReadingListInfo.readingList.products.length > 1) { return; }

        const overlappingReadingList = this.getReadingListsOverlappingWithDragged(x, y);
        if(overlappingReadingList == null){
            this.removeOverlappingElementEmphasis();
        }
        else {
            if(overlappingReadingList != this.draggedReadingListInfo.overlappingReadingList){
                this.removeOverlappingElementEmphasis();
                
                this.draggedReadingListInfo.overlappingReadingList = overlappingReadingList;
                
                if(this.root) {
                    const readingListElement: HTMLElement = this.root.querySelector(`.reading-stack-root[data-reading-list-id='${overlappingReadingList.id}']`);
                    this.draggedReadingListInfo.overlappingHtmlElement = readingListElement;
                    if(readingListElement != null){
                        readingListElement.classList.add("_is-being-dragged-on");
                        readingListElement.classList.add("_is-showing-message-cover");
                        readingListElement.querySelector("._message").textContent = "add to this stack";
                    }
                }
            }
        }
    }

    private removeOverlappingElementEmphasis(){
        if(this.draggedReadingListInfo.overlappingHtmlElement != null){
            this.draggedReadingListInfo.overlappingHtmlElement.classList.remove("_is-being-dragged-on");
            this.draggedReadingListInfo.overlappingHtmlElement.classList.remove("_is-showing-message-cover");
        }

        this.draggedReadingListInfo.overlappingHtmlElement = null;
        this.draggedReadingListInfo.overlappingReadingList = null;
    }

    private getReadingListsOverlappingWithDragged(x: number, y: number): IReadingList {
        if(this.draggedReadingListInfo == null) { return null; }

        const allReadingLists = this.props.store.getDisplayedReadingLists().filter(it => !it.isDummy);

        const l1: {x: number, y: number } = { x: x, y: y};
        const r1: {x: number, y: number } = { x: x + 110, y: y + 133 }

        for(let i = allReadingLists.length - 1; i >= 0; i--){
            const currentReadingList = allReadingLists[i];

            if(currentReadingList.id == this.draggedReadingListInfo.readingList.id) { continue; }

            const l2: {x: number, y: number } = { 
                x: currentReadingList.position != null ? currentReadingList.position.x : 0, 
                y: currentReadingList.position != null ? currentReadingList.position.y : 0
            };

            const r2: {x: number, y: number } = { x: l2.x + 110, y: l2.y + 133 }
            
            if(this.doOverlap(l1, r1, l2, r2)){
                return currentReadingList;
            }
        }

        return null;
    }

    private doOverlap(l1: {x: number, y: number},  
                      r1: {x: number, y: number},  
                      l2: {x: number, y: number},  
                      r2: {x: number, y: number}): boolean {
        const R1 = { topLeft: l1, bottomRight: r1 };
        const R2 = { topLeft: l2, bottomRight: r2 };
        
        return (R1.topLeft.x  <  R2.bottomRight.x) && (R1.bottomRight.x > R2.topLeft.x)  
            && (R1.topLeft.y  <  R2.bottomRight.y) && (R1.bottomRight.y > R2.topLeft.y);
    }

    private handlePointerEnd(e){
        if(this.draggedReadingListInfo != null){
            this.draggedReadingListInfo.htmlElement.classList.remove("_is-being-dragged");
            
            if(this.draggedReadingListInfo.overlappingReadingList != null
            && this.draggedReadingListInfo.readingList.id != this.draggedReadingListInfo.overlappingReadingList.id){
                this.props.store.mergeReadingLists(
                    this.draggedReadingListInfo.readingList,
                    this.draggedReadingListInfo.overlappingReadingList
                );
            }
            else {
                this.props.store.updateReadingListPosition(
                    this.draggedReadingListInfo.readingList, 
                    parseInt(this.draggedReadingListInfo.htmlElement.style.left),
                    parseInt(this.draggedReadingListInfo.htmlElement.style.top),
                );
            }
        }
        
        this.draggedReadingListInfo = null;
        this.unsubscribeFromPointerEvents();
    }

    private handleReadingListRemoval(readingList: IReadingList){
        if(readingList == null) { return; }

        const title = readingList.title || readingList.products[0].title;
        if(confirm(`Delete list ${title} from your desktop?`)){
            this.props.store.deleteReadingList(readingList);

            this.setState({
                selectedReadingList: null
            });
        }
    }

    private unsubscribeFromPointerEvents(){
        document.removeEventListener("mousemove", this.handlePointerMove);
        document.removeEventListener("touchmove", this.handlePointerMove);

        document.removeEventListener("mouseup", this.handlePointerEnd);
        document.removeEventListener("touchend", this.handlePointerEnd);
        document.removeEventListener("mouseleave", this.handlePointerEnd);
    }

    private handleAddToReadingList(readingList: IReadingList, productId: any): void {
        this.props.store.addToReadingListByProductId(readingList, productId).then(() => {
            if(!readingList.title || readingList.title == "unnamed"){
                this.handleReadingListNameChange(readingList);
            }
        });
    }

    private handleSelectReadingList(readingList: IReadingList){
        this.setState({
            selectedReadingList: readingList
        });

        this.props.store.getProductsPercentageFinished(readingList.products.map(it => it.manningId)).then((map) => {
            readingList.products.forEach(product => {
                product.percentageRead = map[product.manningId] || 0;
            })
            
            this.hasBeenMounted && this.forceUpdate();
        });
    }

    private handleUnselectReadingList(){
        if(this.taggedProductListRoot){
            this.taggedProductListRoot.classList.remove("appear");
            setTimeout(() => {
                this.hasBeenMounted && this.setState({
                    selectedReadingList: null
                });
            }, 200)
        }
    }

    private onRemoveProductClick(readingList: IReadingList, productDescription: IProductDescription){
        if(readingList.products.length == 1 
        && readingList.products[0].manningId == productDescription.manningId){
            this.props.store.deleteReadingList(readingList);
            this.handleUnselectReadingList();
            return;
        }
        
        this.props.store.removeProductFromReadingList(readingList, productDescription);
    }

    private handleReadingListNameChange(readingList: IReadingList){
        const newTitle = prompt(readingList.title ? "Change title": "Enter title", readingList.title);
        if(newTitle){
            this.props.store.updateReadingListTitle(readingList, newTitle);
        }
    }

    private onExploreDesktopClick(){
        this.props.store.toggleDesktopTour();
    }
}